import { createContext, useContext, useEffect, useRef } from 'react';

export const DraftContext = createContext('');

const stripFiles = (data) => {
  const out = { ...data };
  Object.keys(out).forEach((k) => {
    const v = out[k];
    if (v instanceof File) out[k] = null;
    else if (v instanceof FileList) out[k] = null;
    else if (Array.isArray(v) && v.some((x) => x instanceof File)) out[k] = null;
  });
  return out;
};

const currentUser = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return u && u.id ? u.id : null;
  } catch {
    return null;
  }
};

export const draftKey = (serviceType) => `pea_draft_${currentUser() || 'guest'}_${serviceType}`;

export function loadDraft(serviceType) {
  try {
    const raw = localStorage.getItem(draftKey(serviceType));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.data) return null;
    return { data: parsed.data, savedAt: parsed.savedAt || null };
  } catch {
    return null;
  }
}

export function clearDraft(serviceType) {
  try {
    localStorage.removeItem(draftKey(serviceType));
  } catch {}
}

export function getDraftMeta(serviceType) {
  const d = loadDraft(serviceType);
  return d ? d.savedAt : null;
}

const notify = (serviceType, savedAt) => {
  try {
    window.dispatchEvent(new CustomEvent('pea-draft-saved', { detail: { serviceType, savedAt } }));
  } catch {}
};

export function useFormDraft(getForm, initialData) {
  const serviceType = useContext(DraftContext);
  const get = useRef(getForm);
  get.current = getForm;
  const hydrated = useRef(false);
  const skipNext = useRef(false);
  const latest = useRef(null);

  useEffect(() => {
    latest.current = get.current()[0];
  });

  useEffect(() => {
    if (!serviceType || initialData) {
      hydrated.current = true;
      return;
    }
    const [, setFormData] = get.current();
    const d = loadDraft(serviceType);
    if (d && d.data && typeof d.data === 'object') {
      const merged = {};
      Object.keys(d.data).forEach((k) => {
        const v = d.data[k];
        if (v !== null && v !== undefined && v !== '') merged[k] = v;
      });
      setFormData((prev) => ({ ...prev, ...merged }));
      skipNext.current = true;
      notify(serviceType, d.savedAt);
    }
    hydrated.current = true;
  }, [serviceType, initialData]);

  useEffect(() => {
    if (!serviceType || !hydrated.current || initialData) return;
    if (skipNext.current) {
      skipNext.current = false;
      return;
    }
    const t = setTimeout(() => {
      try {
        const key = draftKey(serviceType);
        const savedAt = Date.now();
        localStorage.setItem(key, JSON.stringify({ data: stripFiles(latest.current), savedAt }));
        notify(serviceType, savedAt);
      } catch {}
    }, 600);
    return () => clearTimeout(t);
  });

  useEffect(() => {
    return () => {
      if (!serviceType || initialData) return;
      try {
        const key = draftKey(serviceType);
        localStorage.setItem(key, JSON.stringify({ data: stripFiles(latest.current), savedAt: Date.now() }));
      } catch {}
    };
  }, [serviceType, initialData]);
}